import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarDays, ExternalLink, ShieldCheck } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { Seo } from "@/lib/seo";
import { SectionHeading } from "@/components/shared/Shared";

export default function Termin() {
  const { t } = useLang();
  const [calendlyUrl, setCalendlyUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [iframeLoaded, setIframeLoaded] = useState(false);

  useEffect(() => {
    api
      .get("/public/settings")
      .then((r) => setCalendlyUrl(r.data.calendly_url))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="bg-[#F6F7F8]">
      <Seo
        title={t("Termin buchen \u2013 Kostenfreies Erstgespr\u00e4ch", "Book Appointment \u2013 Free Initial Consultation")}
        description={t(
          "Buchen Sie Ihr kostenfreies, unverbindliches Erstgespr\u00e4ch bei Eleven Family Office direkt online \u2013 pers\u00f6nliche Finanzberatung f\u00fcr Vorsorge, Immobilien und Kapitalanlagen.",
          "Book your free, non-binding initial consultation with Eleven Family Office directly online \u2013 personal financial advice for provision, real estate and investments."
        )}
      />
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        <SectionHeading
          eyebrow={t("Terminbuchung", "Appointment Booking")}
          title={t("Buchen Sie Ihr Erstgespr\u00e4ch", "Book your initial consultation")}
          subtitle={t(
            "W\u00e4hlen Sie direkt einen passenden Termin \u2013 unverbindlich, kostenfrei und pers\u00f6nlich. Die Buchung erfolgt \u00fcber Calendly.",
            "Choose a suitable appointment directly \u2013 non-binding, free and personal. Booking is via Calendly."
          )}
        />

        <div className="mt-10 rounded-[8px] border border-border bg-white overflow-hidden" data-testid="calendly-embed">
          {loading ? (
            <div className="p-8 space-y-4">
              <Skeleton className="h-8 w-1/3" />
              <Skeleton className="h-64 w-full" />
            </div>
          ) : calendlyUrl ? (
            <div className="relative">
              {!iframeLoaded && (
                <div className="absolute inset-0 flex items-center justify-center bg-white">
                  <div className="text-center">
                    <div className="mx-auto h-8 w-8 rounded-full border-2 border-[#111318] border-t-transparent animate-spin" />
                    <p className="mt-3 text-sm text-[#6B7280]">{t("Kalender wird geladen\u2026", "Loading calendar\u2026")}</p>
                  </div>
                </div>
              )}
              <iframe
                src={`${calendlyUrl}?embed_domain=${window.location.hostname}&embed_type=Inline`}
                title="Calendly"
                className="w-full h-[680px] border-0"
                onLoad={() => setIframeLoaded(true)}
              />
              <div className="border-t border-border p-4 flex items-center justify-between flex-wrap gap-3 bg-[#F6F7F8]">
                <p className="text-xs text-[#6B7280] flex items-center gap-1.5">
                  <CalendarDays className="h-3.5 w-3.5 text-[#E8241B]" />
                  {t("Termin wird nicht angezeigt? \u00d6ffnen Sie den Kalender extern.", "Calendar not showing? Open it externally.")}
                </p>
                <Button asChild variant="outline" size="sm" className="rounded-[8px] hover:border-[#FF3C20]/60" data-testid="calendly-external-link">
                  <a href={calendlyUrl} target="_blank" rel="noopener noreferrer">
                    {t("Extern \u00f6ffnen", "Open externally")} <ExternalLink className="ml-1 h-3.5 w-3.5" />
                  </a>
                </Button>
              </div>
            </div>
          ) : (
            <div className="p-12 text-center">
              <p className="text-[#3B4452]">{t("Die Terminbuchung ist derzeit nicht verf\u00fcgbar.", "Appointment booking is currently unavailable.")}</p>
              <Button asChild className="mt-5 rounded-[6px] bg-[#FF3C20] text-white hover:bg-[#E8241B] font-semibold" data-testid="termin-fallback-cta">
                <Link to="/anfrage/rueckruf">{t("Stattdessen R\u00fcckruf anfordern", "Request a callback instead")}</Link>
              </Button>
            </div>
          )}
        </div>

        <div className="mt-6 max-w-2xl flex items-start gap-3 rounded-[6px] border border-border bg-white p-4">
          <ShieldCheck className="h-4 w-4 text-[#E8241B] shrink-0 mt-0.5" />
          <p className="text-xs text-[#3B4452] leading-relaxed">
            {t(
              "Bei der Terminbuchung werden Daten an Calendly \u00fcbertragen. Details finden Sie in unserer Datenschutzerkl\u00e4rung.",
              "When booking an appointment, data is transferred to Calendly. Details can be found in our privacy policy."
            )}
          </p>
        </div>
      </div>
    </div>
  );
}
